import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixCloudAutocmsTasksV1TaskServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'editor._base_domain_': [
      {
        srcPath: '/_api/autocms/v1/tasks',
        destPath: '/v1',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/autocms/v1/tasks',
        destPath: '/v1',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/autocms/v1/tasks',
        destPath: '/v1',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/autocms/v1/tasks',
        destPath: '/v1',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/autocms/v1/tasks',
        destPath: '/v1',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/autocms/v1/tasks',
        destPath: '/v1',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/cms/v1/tasks',
        destPath: '/v1',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/autocms/v1/tasks',
        destPath: '/v1',
      },
    ],
    _: [
      {
        srcPath: '/_api/autocms/v1/tasks',
        destPath: '/v1',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_data_tasks';

/** Creates and runs a new background task. */
export function createTask(payload: object): RequestOptionsFactory<any> {
  function __createTask({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'task.startedAt' },
          { path: 'task.finishedAt' },
          {
            path: 'task.updatePublishStatusOptions.scheduleDraftStatusOptions.date',
          },
          {
            path: 'task.updatePublishStatusOptions.schedulePublishedStatusOptions.date',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.data.autocms.v2.background_task',
      method: 'POST' as any,
      methodFqn: 'wix.cloud.autocms.tasks.v1.TaskService.CreateTask',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudAutocmsTasksV1TaskServiceUrl({
        protoPath: '/v1',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'task.startedAt' },
              { path: 'task.finishedAt' },
              {
                path: 'task.updatePublishStatusOptions.scheduleDraftStatusOptions.date',
              },
              {
                path: 'task.updatePublishStatusOptions.schedulePublishedStatusOptions.date',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createTask;
}

/** Lists all existing tasks. */
export function listTasks(payload: object): RequestOptionsFactory<any> {
  function __listTasks({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.autocms.v2.background_task',
      method: 'GET' as any,
      methodFqn: 'wix.cloud.autocms.tasks.v1.TaskService.ListTasks',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudAutocmsTasksV1TaskServiceUrl({
        protoPath: '/v1',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'tasks.startedAt' },
              { path: 'tasks.finishedAt' },
              {
                path: 'tasks.updatePublishStatusOptions.scheduleDraftStatusOptions.date',
              },
              {
                path: 'tasks.updatePublishStatusOptions.schedulePublishedStatusOptions.date',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listTasks;
}

/** Retrieves the specified task. */
export function getTask(payload: object): RequestOptionsFactory<any> {
  function __getTask({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.autocms.v2.background_task',
      method: 'GET' as any,
      methodFqn: 'wix.cloud.autocms.tasks.v1.TaskService.GetTask',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudAutocmsTasksV1TaskServiceUrl({
        protoPath: '/v1/{taskId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'task.startedAt' },
              { path: 'task.finishedAt' },
              {
                path: 'task.updatePublishStatusOptions.scheduleDraftStatusOptions.date',
              },
              {
                path: 'task.updatePublishStatusOptions.schedulePublishedStatusOptions.date',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getTask;
}

/** Cancels an existing task. */
export function cancelTask(payload: object): RequestOptionsFactory<any> {
  function __cancelTask({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.autocms.v2.background_task',
      method: 'DELETE' as any,
      methodFqn: 'wix.cloud.autocms.tasks.v1.TaskService.CancelTask',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCloudAutocmsTasksV1TaskServiceUrl({
        protoPath: '/v1/{taskId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'task.startedAt' },
              { path: 'task.finishedAt' },
              {
                path: 'task.updatePublishStatusOptions.scheduleDraftStatusOptions.date',
              },
              {
                path: 'task.updatePublishStatusOptions.schedulePublishedStatusOptions.date',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __cancelTask;
}
